import React from "react";
import { Users, FileText, Camera, HeartHandshake, ShieldCheck, TrendingDown } from "lucide-react";
import { calcAdoptionFunnel } from "./helpers";
import WidgetInfoTooltip from "./WidgetInfoTooltip";

const STAGE_ICONS = {
  registered: Users,
  f1: FileText,
  facescan: Camera,
  lifestyle: HeartHandshake,
  full: ShieldCheck,
};

const STAGE_DETAILS = {
  registered: {
    title: "1. Censo Convocado & Registrado",
    what: "Colaboradores con cuenta creada en el portal corporativo WE360.",
    calculation: "Total cuentas registradas asociadas al cliente.",
    standard: "Padrón Ocupacional de Vigilancia de la Salud (Convenio 161 OIT).",
    relevance: "Universo base para el cálculo de cobertura e indicadores epidemiológicos.",
  },
  f1: {
    title: "2. Formulario Basal Clínico (F1)",
    what: "Captura de antecedentes patológicos, sintomatología y biometría inicial.",
    calculation: "Colaboradores que completaron el formulario F1 Clínico / Total Censo.",
    standard: "Historia Clínica Laboral Ocupacional estandarizada OMS / OIT.",
    relevance: "Estratificación de riesgo basal e identificación de patología previa.",
  },
  facescan: {
    title: "3. Escaneo Facial Óptico (rPPG)",
    what: "Medición contactless de biomarcadores hemodinámicos y autonómicos vía cámara.",
    calculation: "Colaboradores con al menos 1 escaneo rPPG facial exitoso / Total Censo.",
    standard: "Fotopletismografía remota computacional & Algoritmo FaceHeart Vitals.",
    relevance: "Telemetría objetiva sin necesidad de equipamiento médico invasivo en planta.",
  },
  lifestyle: {
    title: "4. Hábitos & Estilo de Vida (F3/F4)",
    what: "Evaluación estandarizada de actividad física (IPAQ) e higiene del sueño.",
    calculation: "Colaboradores con módulos de estilo de vida respondidos / Total Censo.",
    standard: "IPAQ Research Committee & National Sleep Foundation Consensus.",
    relevance: "Identificación de factores de riesgo modificables en la rutina diaria.",
  },
  full: {
    title: "5. Cobertura Integral 360°",
    what: "Perfil holístico completo con todas las fuentes de datos validadas para el triaje HI-OS.",
    calculation: "Colaboradores con F1 + rPPG + F3/F4 completados al 100% / Total Censo.",
    standard: "Protocolo de Vigilancia Epidemiológica de Alta Fidelidad OIT/OMS.",
    relevance: "Habilita la asignación automatizada de prioridades clínicas y planes de salud.",
  },
};

export default function AdoptionFunnelCard({ employees = [], forceSimulated = false }) {
  const funnel = calcAdoptionFunnel(employees, forceSimulated);
  const conversionRate = funnel.stages[funnel.stages.length - 1].percent;

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm relative"
      data-testid="adoption-funnel-card"
    >
      {/* Cabecera del Embudo */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200">
              <Users size={12} />
              Protocolo Epidemiológico SST
            </span>
            <span className="text-xs text-slate-400">
              Censo evaluado: <strong>{funnel.total}</strong> colaboradores
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <h3 className="text-base font-bold text-slate-800">
              Embudo de Adopción & Cobertura de Tamizaje
            </h3>
            <WidgetInfoTooltip
              title="Embudo de Adopción & Cobertura de Tamizaje SST"
              align="left"
              what="Monitoreo secuencial del avance de la plantilla a través de las 5 fases estandarizadas del programa de medicina preventiva."
              calculation="Tasa de retención por etapa: (Colaboradores que completaron el hito / Total censo invitado) × 100. Los drop-offs calculan la deserción entre etapas consecutivas."
              standard="Normas de Medicina del Trabajo OIT C161 & Guías de Vigilancia Epidemiológica Ocupacional OMS/CDC."
              relevance="Permite a Recursos Humanos y SST detectar cuellos de botella operativos (ej. falta de escaneo facial rPPG) y accionar campañas internas enfocadas."
            />
          </div>
          <p className="text-xs text-slate-500">
            Seguimiento de la retención y completitud de las etapas del protocolo de salud ocupacional.
          </p>
        </div>

        <div className="flex items-center gap-3 bg-slate-50 border border-slate-200/60 rounded-xl px-3.5 py-2">
          <div className="text-right">
            <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
              Cobertura 360° Apta
            </p>
            <p className="text-lg font-extrabold text-emerald-600 leading-tight">
              {conversionRate}%
            </p>
          </div>
          <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
            <ShieldCheck size={20} />
          </div>
        </div>
      </div>

      {/* Flujo Visual del Embudo (5 Etapas) */}
      <div className="space-y-3.5">
        {funnel.stages.map((stage, idx) => {
          const IconComponent = STAGE_ICONS[stage.id] || Users;
          const prevStage = idx > 0 ? funnel.stages[idx - 1] : null;
          const dropOff = prevStage ? prevStage.percent - stage.percent : 0;

          return (
            <div key={stage.id} className="group">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2.5">
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold transition-transform group-hover:scale-105"
                    style={{ backgroundColor: stage.bg, color: stage.color }}
                  >
                    <IconComponent size={14} />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-bold text-slate-800">
                      {stage.title}
                    </span>
                    {STAGE_DETAILS[stage.id] && (
                      <WidgetInfoTooltip
                        title={STAGE_DETAILS[stage.id].title}
                        size="sm"
                        align="left"
                        what={STAGE_DETAILS[stage.id].what}
                        calculation={STAGE_DETAILS[stage.id].calculation}
                        standard={STAGE_DETAILS[stage.id].standard}
                        relevance={STAGE_DETAILS[stage.id].relevance}
                      />
                    )}
                    <span className="text-[11px] text-slate-400 ml-1 hidden md:inline">
                      • {stage.description}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-right self-end sm:self-auto">
                  {dropOff > 0 && (
                    <span className="text-[10px] font-semibold text-rose-500 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-100 flex items-center gap-0.5">
                      <TrendingDown size={10} /> -{dropOff}%
                    </span>
                  )}
                  <span className="text-xs text-slate-500 font-medium">
                    {stage.count} colab.
                  </span>
                  <span
                    className="text-xs font-extrabold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: stage.bg, color: stage.color }}
                  >
                    {stage.percent}%
                  </span>
                </div>
              </div>

              {/* Barra de Progreso del Embudo */}
              <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden p-0.5 border border-slate-200/50">
                <div
                  className="h-2 rounded-full transition-all duration-700 shadow-sm"
                  style={{
                    width: `${Math.max(4, stage.percent)}%`,
                    backgroundColor: stage.color,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Nota Operativa al Pie */}
      <div className="mt-5 pt-3.5 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between text-xs text-slate-500 gap-2">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          Los colaboradores con cobertura 360° cuentan con triaje médico asistido por IA (HI-OS).
        </span>
        <span className="text-[11px] text-slate-400">
          Objetivo de acreditación corporativa: <strong>≥ 75%</strong>
        </span>
      </div>
    </div>
  );
}
